import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useMemo, useState } from "react";
import { BookOpen, Search, X, Library as LibraryIcon } from "lucide-react";
import { RESOURCES, type Resource } from "@/lib/library-resources";
import { FavoriteButton } from "@/components/FavoriteButton";
import { StarRating } from "@/components/StarRating";

export const Route = createFileRoute("/workbooks")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Workbook Library — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Trauma-informed workbooks for CPTSD, PTSD, nervous system regulation, inner child healing, attachment, trauma bonds, and music-led recovery.",
      },
      { property: "og:title", content: "Workbook Library — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "A growing collection of trauma-informed workbooks to support your recovery journey.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/workbooks" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/workbooks" }],
  }),
  component: withMemberGate(WorkbooksPage, "Workbooks"),
});

function isWorkbook(r: Resource): boolean {
  const hay = `${r.title} ${r.slug} ${r.blurb}`.toLowerCase();
  return hay.includes("workbook");
}

function matches(r: Resource, q: string) {
  if (!q) return true;
  const hay = `${r.title} ${r.blurb} ${r.topics.join(" ")}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => hay.includes(term));
}

function WorkbooksPage() {
  const [query, setQuery] = useState("");
  const workbooks = useMemo(
    () => RESOURCES.filter(isWorkbook).filter((r) => matches(r, query)),
    [query],
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:py-10">
      <header className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <LibraryIcon className="h-3.5 w-3.5" /> Recovery Library
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground md:text-5xl">
          📚 Workbook Library
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Trauma-informed workbooks to support your recovery — at your own pace, in your own time.
        </p>
        <div className="mt-4 flex items-center justify-center gap-2 text-xs">
          <Link to="/library" className="text-[var(--gold-deep)] underline-offset-2 hover:underline">
            Browse the full Recovery Library
          </Link>
        </div>
      </header>

      <section aria-label="Search workbooks" className="mb-8">
        <label className="relative mx-auto block max-w-2xl">
          <span className="sr-only">Search workbooks</span>
          <Search
            className="pointer-events-none absolute start-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--gold-deep)]"
            strokeWidth={2.25}
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search workbooks…"
            className="w-full rounded-2xl border-2 border-gold/40 bg-card py-3.5 ps-14 pe-12 text-base text-foreground outline-none transition-all placeholder:text-muted-foreground/80 focus:border-[var(--gold-deep)] focus:shadow-[var(--shadow-gold)]"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute end-3 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </label>
      </section>

      {workbooks.length === 0 ? (
        <p className="mx-auto max-w-md text-center text-sm text-muted-foreground">
          No workbooks match your search.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {workbooks.map((r) => (
            <div
              key={r.slug}
              className="group relative flex flex-col overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[var(--shadow-gold)]"
            >
              {r.href ? (
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-[2/3] overflow-hidden bg-cream/40"
                >
                  {r.cover ? (
                    <img
                      src={r.cover}
                      alt={`${r.title} cover`}
                      loading="lazy"
                      className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-4xl">
                      {r.emoji ?? "📘"}
                    </div>
                  )}
                </a>
              ) : (
                <Link
                  to="/library/$slug"
                  params={{ slug: r.slug }}
                  className="block aspect-[2/3] overflow-hidden bg-cream/40"
                >
                  {r.cover ? (
                    <img
                      src={r.cover}
                      alt={`${r.title} cover`}
                      loading="lazy"
                      className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-4xl">
                      {r.emoji ?? "📘"}
                    </div>
                  )}
                </Link>
              )}
              <FavoriteButton
                storageKey="rt:fav-library"
                id={r.slug}
                label={r.title}
                sub={r.blurb}
                to={r.href ?? `/library/${r.slug}`}
                size="sm"
                className="absolute end-2 top-2 h-7 w-7 shadow-sm backdrop-blur"
              />
              <div className="flex flex-1 flex-col gap-2 p-3">
                <span className="inline-flex w-fit items-center rounded-full bg-gold/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
                  Workbook
                </span>
                <h3 className="line-clamp-3 font-display text-sm leading-snug text-foreground">
                  {r.title}
                </h3>
                <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                  {r.blurb}
                </p>
                <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                  <StarRating id={r.slug} storageKey="rt:rating-library" size="sm" />
                  {r.href ? (
                    <a
                      href={r.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[var(--gold-deep)]"
                      aria-label={`Open ${r.title}`}
                    >
                      <BookOpen className="h-3 w-3" /> Open
                    </a>
                  ) : (
                    <Link
                      to="/library/$slug"
                      params={{ slug: r.slug }}
                      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[var(--gold-deep)]"
                      aria-label={`Read ${r.title}`}
                    >
                      <BookOpen className="h-3 w-3" /> Read
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
